import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const benefits = [
  {
    title: "Competitive Pay",
    description: "Above-market hourly wages with regular raises based on performance and tenure.",
    icon: "ri-money-dollar-circle-line",
  },
  {
    title: "Flexible Scheduling",
    description: "Choose shifts that fit your life. Part-time, full-time, and weekend options available.",
    icon: "ri-calendar-check-line",
  },
  {
    title: "Paid Training",
    description: "Comprehensive onboarding and ongoing education at no cost to you. Learn while you earn.",
    icon: "ri-graduation-cap-line",
  },
  {
    title: "Wellness Benefits",
    description: "Full-time caregivers qualify for wellness benefits and supplemental coverage options.",
    icon: "ri-heart-pulse-line",
  },
  {
    title: "Referral Bonuses",
    description: "Earn bonuses for every qualified caregiver you refer who joins our team.",
    icon: "ri-user-add-line",
  },
  {
    title: "Career Growth",
    description: "Clear advancement paths from caregiver to team lead, trainer, and management roles.",
    icon: "ri-bar-chart-box-line",
  },
];

const openPositions = [
  {
    title: "Caregiver",
    type: "Full-time / Part-time",
    location: "Naperville, IL & Surrounding Areas",
    requirements: ["Compassionate and reliable personality", "High school diploma or GED", "Reliable transportation"],
    description: "Provide direct personal care to clients including bathing, dressing, grooming, and mobility assistance. Work one-on-one in clients homes with flexible scheduling.",
  },
  {
    title: "Companion Care Assistant",
    type: "Full-time / Part-time",
    location: "Cook, Kane, Will, DuPage Counties",
    requirements: ["Friendly and patient demeanor", "Valid drivers license preferred", "Available evenings and weekends"],
    description: "Assist clients with daily living activities, meal preparation, light housekeeping, and companionship. Ideal for those starting their caregiving career.",
  },
  {
    title: "Personal Support Assistant",
    type: "Part-time / Flexible",
    location: "All Service Areas",
    requirements: ["High school diploma or GED", "Valid drivers license", "Excellent communication skills"],
    description: "Provide companionship, conversation, transportation to appointments, and assistance with errands. Focus on emotional wellbeing and social engagement.",
  },
  {
    title: "Homemaker Assistant",
    type: "Part-time / Flexible",
    location: "Kendall, LaSalle, DuPage Counties",
    requirements: ["Organized and detail-oriented", "Ability to work independently", "Reliable and punctual"],
    description: "Support clients with light housekeeping, meal preparation, laundry, and home organization tasks. Help create a safe and comfortable living environment.",
  },
];

export default function Careers() {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    fetch(form.action, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data.toString(),
    })
      .then(() => setFormStatus("success"))
      .catch(() => setFormStatus("error"));
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20and%20inviting%20home%20care%20workspace%20background%2C%20soft%20green%20and%20beige%20abstract%20organic%20shapes%2C%20professional%20caregiving%20atmosphere%2C%20calming%20minimal%20design%2C%20no%20text%2C%20gentle%20gradients&width=1600&height=500&seq=careerhero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-300 mb-3 block">
            Join Our Team
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Careers at Sycamore Oasis
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            Make a real difference in peoples lives. Join a team that values compassion, professionalism, and growth in non-medical home care.
          </p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="benefits" ref={setRef("benefits")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("benefits", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Benefits
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Why Work With Us?
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              We invest in our caregivers because they are the heart of everything we do.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {benefits.map((benefit, i) => (
              <div
                key={benefit.title}
                className={`bg-soft-gray rounded-xl p-6 border border-gray-100 hover:border-brand-green-200 hover:shadow-md transition-all duration-300 ${
                  isVisible["benefits"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${benefit.icon} text-xl text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="jobs" ref={setRef("jobs")}>
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          <div className={animateClass("jobs", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Now Hiring
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Open Positions
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Find a role that matches your skills and passion for helping others.
            </p>
          </div>

          <div className="space-y-4">
            {openPositions.map((job, i) => {
              const isExpanded = expandedJob === job.title;
              return (
                <div
                  key={job.title}
                  className={`bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-brand-green-200 transition-all duration-300 ${
                    isVisible["jobs"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="p-5 md:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{job.title}</h3>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-time-line text-gray-400" />
                            </div>
                            {job.type}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-map-pin-line text-gray-400" />
                            </div>
                            {job.location}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => setExpandedJob(isExpanded ? null : job.title)}
                        className="shrink-0 inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-brand-green-600 hover:text-brand-green-700 bg-brand-green-50 hover:bg-brand-green-100 rounded-full transition-all whitespace-nowrap"
                      >
                        {isExpanded ? "Show Less" : "View Details"}
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                        </div>
                      </button>
                    </div>

                    <div
                      className={`overflow-hidden transition-all duration-500 ${
                        isExpanded ? "max-h-[600px] opacity-100 mt-4 pt-4 border-t border-gray-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <p className="text-sm text-gray-500 leading-relaxed mb-4">{job.description}</p>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Requirements:</h4>
                      <ul className="space-y-1.5 mb-4">
                        {job.requirements.map((req) => (
                          <li key={req} className="flex items-start gap-2 text-sm text-gray-500">
                            <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                              <i className="ri-checkbox-circle-line text-brand-green-500 text-xs" />
                            </div>
                            {req}
                          </li>
                        ))}
                      </ul>
                      <a
                        href="#apply"
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-brand-green-600 text-white font-semibold text-sm rounded-full hover:bg-brand-green-700 transition-all whitespace-nowrap"
                      >
                        Apply for This Role
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-xs" />
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-14 md:py-20 bg-warm-beige" data-animate-id="apply" ref={setRef("apply")} id="apply">
        <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
          <div className={animateClass("apply", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Apply Now
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Join Our Team
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Fill out the form below and our hiring team will reach out within 2 business days.
            </p>
          </div>

          <div className={animateClass("apply", "bg-soft-gray rounded-2xl p-6 md:p-8 border border-gray-100")}>
            {formStatus === "success" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-brand-green-100 flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-brand-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Application Submitted!</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Thank you for your interest. Our hiring team will review your application and contact you within 2 business days.
                </p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="px-6 py-2.5 bg-brand-green-600 text-white font-semibold text-sm rounded-full hover:bg-brand-green-700 transition-all"
                >
                  Submit Another Application
                </button>
              </div>
            ) : (
              <form
                action="https://readdy.ai/api/form/d7r764kcmq04e0g4e4bg"
                method="POST"
                data-readdy-form
                id="careers-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-xs font-medium text-gray-700 mb-1.5">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                      placeholder="Jane"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-xs font-medium text-gray-700 mb-1.5">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                    placeholder="jane@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                    placeholder="773-699-7894"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Position Applying For *
                  </label>
                  <select
                    id="position"
                    name="position"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all bg-white"
                  >
                    <option value="">Select a position...</option>
                    <option value="caregiver">Caregiver</option>
                    <option value="companion">Companion Care Assistant</option>
                    <option value="personal-support">Personal Support Assistant</option>
                    <option value="homemaker">Homemaker Assistant</option>
                    <option value="other">Other / General Interest</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="experience" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Years of Experience
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all bg-white"
                  >
                    <option value="">Select experience level...</option>
                    <option value="0">No experience — willing to train</option>
                    <option value="0-1">Less than 1 year</option>
                    <option value="1-3">1 – 3 years</option>
                    <option value="3-5">3 – 5 years</option>
                    <option value="5+">5+ years</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Why do you want to join Sycamore Oasis? (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all resize-none"
                    placeholder="Tell us about yourself and why you are interested in caregiving..."
                  />
                  <p className="text-[11px] text-gray-400 mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full px-6 py-3 bg-brand-green-600 hover:bg-brand-green-700 disabled:bg-brand-green-400 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap"
                >
                  {formStatus === "submitting" ? "Submitting..." : "Submit Application"}
                </button>

                {formStatus === "error" && (
                  <p className="text-sm text-red-600 text-center">
                    Something went wrong. Please try again or contact us directly.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-user-heart-line text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Questions About Working With Us?
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Our hiring team is happy to answer any questions about schedules, training, pay, or the application process.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Contact Hiring Team
            </Link>
            <a
              href="mailto:careers@sycamoreoasis.com"
              rel="nofollow"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              Email careers@sycamoreoasis.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}